/*
 * ajax-cart.js — ported from orion-v3.0's src/js/includes/ajax-cart.js.
 *
 * Drives the AJAX cart engine for the cart drawer + cart page:
 *   - Intercepts fetch() calls to /cart/add|change|update|clear and
 *     re-renders any registered [data-ajax-cart-section] regions
 *     from the response's `sections` payload.
 *   - Preserves UI state (open accordion, focused field) across
 *     re-render via [data-ajax-cart-static-element].
 *   - Updates count bubbles + bound elements via
 *     [data-ajax-cart-bind-state="cart.item_count"].
 *   - Defines <sa-cart-item>, <sa-product-form>, <sa-cart-note>
 *     custom elements for per-item quantity / remove and note edit.
 *
 * Differences from v3.0:
 *   - Lodash `debounce` replaced with an inline implementation
 *     since H3.0 ships assets unbundled.
 *   - No ES module imports — exports are exposed via window.ajaxCart
 *     so cart-drawer.js (a separate file) can reach them at runtime.
 */

console.log('Studio almond ajax-cart.js loaded');

// v3.0 read cart URLs from window.routes (set by its own theme.liquid).
// H3.0 exposes them on Theme.routes — mirror onto window.routes so the
// rest of this engine works unchanged.
if (!window.routes && typeof Theme !== 'undefined' && Theme.routes) {
  window.routes = Theme.routes;
}

function debounce(fn, wait) {
  let timer;
  return function debounced(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

function toggleCartDrawer(show) {
  document.body.classList.toggle('cart-drawer--show', show);
}

function getSectionsToRender() {
  const ajaxCartSectionElements = document.querySelectorAll(
    '[data-ajax-cart-section]',
  );

  return Array.from(ajaxCartSectionElements).reduce((sectionMap, el) => {
    const shopifySection = el.closest('.shopify-section');

    const id = shopifySection?.id?.replace('shopify-section-', '');
    const sectionNumber = Number(el.dataset.ajaxCartSection);

    const sectionNumbers = sectionMap.get(id) ?? [];

    if (!sectionNumbers.includes(sectionNumber)) {
      sectionNumbers.push(sectionNumber);
    }

    sectionMap.set(id, sectionNumbers);

    return sectionMap;
  }, new Map());
}

function renderSections(sections) {
  console.debug('Rendering sections', sections);

  const parser = new DOMParser();

  const sectionsToRender = getSectionsToRender();

  for (const sectionId of sectionsToRender.keys()) {
    const html = sections[sectionId];
    if (!html) {
      continue;
    }

    try {
      const parsedDOM = parser.parseFromString(html, 'text/html');
      const newSections = Array.from(
        parsedDOM.querySelectorAll('[data-ajax-cart-section]'),
      );

      const sectionNumbers = sectionsToRender.get(sectionId) ?? [
        `#shopify-section-${sectionId}`,
      ];
      for (const sectionNumber of sectionNumbers) {
        const newHtml = newSections[sectionNumber]?.innerHTML;
        const elementToReplace = document.querySelector(
          `[data-ajax-cart-section="${sectionNumber}"]`,
        );

        if (elementToReplace && newHtml) {
          const staticElementsState =
            preserveStaticElementsState(elementToReplace);
          elementToReplace.innerHTML = newHtml;
          restoreStaticElementsState(elementToReplace, staticElementsState);
        }
      }
    } catch (e) {
      console.log(`Error rendering section ${sectionId}: ${e.message}`);
    }
  }
}

function preserveStaticElementsState(container) {
  const staticElements = container.querySelectorAll(
    '[data-ajax-cart-static-element]',
  );
  const stateMap = new Map();

  staticElements.forEach((el) => {
    const elementId = el.dataset.ajaxCartStaticElement;
    if (!elementId) return;

    const state = {};

    if (el.type === 'checkbox' || el.type === 'radio') {
      state.checked = el.checked;
    }

    if (el.value !== undefined) {
      state.value = el.value;
    }

    stateMap.set(elementId, state);
  });

  return stateMap;
}

function restoreStaticElementsState(container, stateMap) {
  stateMap.forEach((state, elementId) => {
    const el = container.querySelector(
      `[data-ajax-cart-static-element="${elementId}"]`,
    );

    if (!el) return;

    if (state.checked !== undefined) {
      el.checked = state.checked;
    }

    if (state.value !== undefined) {
      el.value = state.value;
    }
  });
}

function updateBoundElements(cartData) {
  const boundElements = document.querySelectorAll(
    '[data-ajax-cart-bind-state]',
  );

  const data = {cart: cartData};

  boundElements.forEach((el) => {
    const bindPath = el.getAttribute('data-ajax-cart-bind-state');
    if (!bindPath) return;

    const pathParts = bindPath.split('.');
    let value = data;

    for (const part of pathParts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        value = null;
        break;
      }
    }

    if (value !== null && value !== undefined) {
      if (bindPath === 'cart.item_count') {
        const isDesktop = el.classList.contains('show-desktop');
        if (isDesktop) {
          el.textContent = `(${value})`;
        } else {
          el.textContent = value > 0 ? value : '';
        }
      } else {
        el.textContent = value;
      }

      el.setAttribute('data-ajax-cart-bind-value', value);
    }

    const bubbles = document.querySelectorAll('.cart-count-bubble');

    bubbles.forEach((bubble) => {
      bubble.classList.toggle('show-bubble', cartData.item_count > 0);
    });
  });
}

/* Roadmap GWP mutates the cart server-side without going through our
 * fetch interceptor below (which only fires on /cart/add | /cart/change
 * | /cart/update | /cart/clear). When GWP signals a change we re-pull
 * /cart.js + the registered section markup and apply the same bound-
 * elements / body-class / cart:updated work the interceptor would
 * normally do — so the navbar count, empty-state class, and any
 * cart:updated listeners stay in sync. */
async function refreshCart() {
  const sectionIds = Array.from(getSectionsToRender().keys()).filter(Boolean);
  if (sectionIds.length === 0) return;

  const root = window.Shopify?.routes?.root || '/';

  try {
    const [cartRes, sectionsRes] = await Promise.all([
      fetch(`${root}cart.js`),
      fetch(`${root}?sections=${sectionIds.join(',')}`),
    ]);

    if (sectionsRes.ok) {
      renderSections(await sectionsRes.json());
    }

    if (cartRes.ok) {
      const cartData = await cartRes.json();
      document.body.classList.toggle(
        'js-ajax-cart-empty',
        cartData.item_count === 0,
      );
      updateBoundElements(cartData);
      document.dispatchEvent(
        new CustomEvent('cart:updated', {detail: {cart: cartData}}),
      );
    }
  } catch (e) {
    console.warn('[ajax-cart] refreshCart failed', e);
  }
}

window.ajaxCart = {
  renderSections,
  getSectionsToRender,
  toggleCartDrawer,
  updateBoundElements,
  refreshCart,
};

let initialCartData;
function initAjaxCart() {
  const el = document.querySelector('[data-ajax-cart-initial-state]');
  if (el) {
    initialCartData = JSON.parse(el.textContent);
    document.body.classList.toggle(
      'js-ajax-cart-empty',
      initialCartData.item_count === 0,
    );

    updateBoundElements(initialCartData);

    const cartUpdatedEvent = new CustomEvent('cart:updated', {
      detail: {cart: initialCartData},
    });
    document.dispatchEvent(cartUpdatedEvent);
  }

  document.querySelectorAll('[data-ajax-cart-section]').forEach((el, i) => {
    el.dataset.ajaxCartSection = i;
  });

  document.addEventListener(
    'cart:updated',
    (event) => {
      if (event.detail && event.detail.cart) {
        updateBoundElements(event.detail.cart);
      }
    },
    {capture: true},
  );
}

// scripts.liquid loads us with `defer`, so DOMContentLoaded may already
// have fired by the time we run. Branch on readyState rather than
// blindly subscribing (a listener added after the event silently misses
// it).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAjaxCart);
} else {
  initAjaxCart();
}

// Intercept cart requests and rerender sections
(function (ns, fetch) {
  ns.fetch = async function () {
    const requestUrl = arguments[0] ?? '';
    const response = await fetch.apply(this, arguments);

    const isCartMutation =
      typeof requestUrl === 'string' &&
      (requestUrl.includes('/cart/add') ||
        requestUrl.includes('/cart/change') ||
        requestUrl.includes('/cart/update') ||
        requestUrl.includes('/cart/clear'));

    if (isCartMutation && response.ok) {
      let cartData;

      try {
        const data = await response.clone().json();

        if (typeof data.item_count === 'number') {
          cartData = data;
        } else {
          const cartRes = await fetch('/cart.js');
          cartData = await cartRes.json();
        }

        // H3.0's product-form-component POSTs to /cart/add with only
        // its own cart-items-component section IDs in `sections`, so
        // the response never carries our cart-drawer / main-cart
        // markup. Backfill any section we registered via
        // [data-ajax-cart-section] but didn't receive, using
        // Shopify's Section Rendering API (`?sections=<csv>`), then
        // render the merged result.
        const receivedSections = data.sections || {};
        const registeredIds = Array.from(
          getSectionsToRender().keys(),
        ).filter(Boolean);
        const missingIds = registeredIds.filter(
          (id) => !(id in receivedSections),
        );

        let combinedSections = {...receivedSections};
        if (missingIds.length > 0) {
          try {
            const sectionsUrl = new URL(window.location.href);
            sectionsUrl.searchParams.set('sections', missingIds.join(','));
            const sectionsRes = await fetch(sectionsUrl.toString());
            if (sectionsRes.ok) {
              const fetched = await sectionsRes.json();
              combinedSections = {...combinedSections, ...fetched};
            }
          } catch (sectionErr) {
            console.error('Failed to fetch missing cart sections', sectionErr);
          }
        }

        if (Object.keys(combinedSections).length > 0) {
          renderSections(combinedSections);
        }

        document.body.classList.toggle(
          'js-ajax-cart-empty',
          cartData.item_count === 0,
        );

        updateBoundElements(cartData);

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {cart: cartData},
          }),
        );

        // Drawer-slides-in feedback for adds (the v3.0 post-add UX).
        // Only on /cart/add (skip change/update/clear so quantity
        // tweaks inside the drawer don't re-open it), and skip on
        // /cart itself where the drawer would overlay the cart page.
        const isAddRequest = requestUrl.includes('/cart/add');
        const onCartPage =
          typeof Theme !== 'undefined' && Theme.template?.name === 'cart';
        if (isAddRequest && !onCartPage) {
          toggleCartDrawer(true);
        }
      } catch (e) {
        console.error('Cart update failed', e);
      }
    }

    const newResponse = new Response(response.body, response);
    Object.defineProperty(newResponse, 'url', {value: requestUrl});

    return newResponse;
  };
})(window, window.fetch);

/**
 * <sa-cart-item> — per-line item quantity + remove controls.
 */
class CartItem extends HTMLElement {
  constructor() {
    super();

    if (this.getAttribute('line') == null) {
      console.error(
        '[CartItem] Missing cart-item attribute: line. e.g. <sa-cart-item line="{{ item_index }}" />',
      );
    } else {
      this.line = this.getAttribute('line');
    }

    this.addEventListener('click', this.onCartRequestButtonClick.bind(this));
    this.addEventListener('click', this.onCartQuantityButtonClick.bind(this));
    // 'change' (not 'input'): input fires per keystroke, so a multi-digit
    // quantity committed its first digit, and the /cart/change re-render
    // then innerHTML'd away the field being typed into — collapsing any
    // typed quantity to that first digit. 'change' fires once, on blur or
    // Enter, with the finished value.
    this.addEventListener('change', this.onCartQuantityInputChange.bind(this));

    this.qtyInput = this.querySelector('[data-ajax-cart-quantity-input]');
    this.updateItemQuantity = debounce(this.updateItemQuantity.bind(this), 300);
    this.updateOriginalQuantity();
  }

  onCartQuantityButtonClick(event) {
    const el = event.target?.closest(
      '[data-ajax-cart-quantity-plus], [data-ajax-cart-quantity-minus]',
    );
    if (el == null || this.qtyInput == null) {
      return;
    }
    event.preventDefault();

    const increment = el.dataset.ajaxCartQuantityPlus !== undefined ? 1 : -1;
    const newQuantity = parseInt(this.qtyInput.value, 10) + increment;

    this.qtyInput.value = newQuantity;
    this.updateItemQuantity(newQuantity);
  }

  async onCartRequestButtonClick(event) {
    const el = event.target?.closest('[data-ajax-cart-request-button]');
    if (el == null || el.href == null) {
      return;
    }
    event.preventDefault();

    if (this.abortController != null) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const {href} = el;

    // Parse line / quantity out of the href and forward them in the
    // JSON body. The previous version only sent `sections` in the body
    // and relied on Shopify reading the URL query string for the
    // mutation params — that's ambiguous when Content-Type is
    // application/json and was clearing the entire cart on a single
    // remove click (no line/quantity in the body → Shopify treated
    // the call as an unbounded change). Mirrors the body shape that
    // `updateItemQuantity` (the working +/- path) uses.
    const url = new URL(href, window.location.origin);
    const lineParam = url.searchParams.get('line');
    const quantityParam = url.searchParams.get('quantity');

    this.setLoading(true);

    try {
      await fetch(url.pathname, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          line: lineParam ? parseInt(lineParam, 10) : undefined,
          quantity: quantityParam != null ? parseInt(quantityParam, 10) : undefined,
          sections: Array.from(getSectionsToRender().keys()),
        }),
        signal: this.abortController.signal,
      });
    } catch (e) {
      console.error(e);
    } finally {
      this.setLoading(false);
      this.abortController = null;
    }
  }

  setLoading(isLoading) {
    this.classList.toggle('loading', isLoading);
  }

  async onCartQuantityInputChange(event) {
    const el = event.target?.closest('[data-ajax-cart-quantity-input]');
    if (el == null) {
      return;
    }

    const newQuantity = parseInt(el.value);
    if (!Number.isNaN(newQuantity)) {
      this.updateItemQuantity(newQuantity);
    }
  }

  updateOriginalQuantity() {
    if (this.qtyInput) {
      this.originalQuantity = this.qtyInput.value;
    }
  }

  async updateItemQuantity(newQuantity) {
    this.setLoading(true);

    if (this.abortController != null) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    try {
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          line: this.line,
          quantity: newQuantity,
          sections: Array.from(getSectionsToRender().keys()),
        }),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        if (this.qtyInput && this.originalQuantity !== undefined) {
          this.qtyInput.value = this.originalQuantity;
        }
      } else {
        this.updateOriginalQuantity();
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setLoading(false);
      this.abortController = null;
    }
  }
}

customElements.define('sa-cart-item', CartItem);

/**
 * <sa-product-form> — wraps a product form, posts AJAX-style.
 */
class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type=submit]');

    if (this.form == null) {
      console.error(
        '[ERROR] No product form inside the <sa-product-form /> element.',
      );
      return;
    }

    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  async onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton?.getAttribute('aria-disabled') === 'true') {
      return;
    }

    this.setLoading(true);

    const sectionsToRender = getSectionsToRender();
    const sectionIds = Array.from(sectionsToRender.keys());

    const formData = new FormData(this.form);
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds);
    }

    try {
      await fetch(`${window.routes.cart_add_url}`, {
        method: 'post',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
      });
    } catch (e) {
      console.error(e);
    } finally {
      // Drawer auto-open is handled centrally by the /cart/add
      // fetch interceptor below — covers both this sa-product-form
      // path (recommendations card) and H3.0's product-form-component
      // path (PDP). No explicit toggle here so the open code path
      // lives in exactly one place.
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (this.submitButton) {
      this.classList.toggle('loading', isLoading);
      this.submitButton.setAttribute('aria-disabled', isLoading);
    }
  }
}

customElements.define('sa-product-form', ProductForm);

/**
 * <sa-cart-note> — single textarea, POSTs to /cart/update.js on blur.
 */
class CartNote extends HTMLElement {
  constructor() {
    super();

    this.textarea = this.querySelector('[data-ajax-cart-property-input]');

    if (!this.textarea) {
      console.error(
        '[CartNote] Missing textarea with data-ajax-cart-property-input attribute',
      );
      return;
    }

    this.originalValue = this.textarea.value.trim();
    this.textarea.addEventListener('blur', this.onNoteBlur.bind(this));
    this.abortController = null;
  }

  async onNoteBlur(event) {
    const noteValue = event.target.value.trim();

    if (noteValue === this.originalValue) {
      return;
    }

    if (this.abortController != null) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    this.setLoading(true);

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          note: noteValue,
          sections: Array.from(getSectionsToRender().keys()),
        }),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        console.error('Failed to update cart note');
        return;
      }

      try {
        const data = await response.clone().json();

        if (data.sections) {
          renderSections(data.sections);
        }

        if (typeof data.item_count === 'number') {
          updateBoundElements(data);
          document.body.classList.toggle(
            'js-ajax-cart-empty',
            data.item_count === 0,
          );
        }
      } catch (parseError) {
        console.error('Error parsing cart note response', parseError);
      }

      this.originalValue = noteValue;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error updating cart note:', error);
      }
    } finally {
      this.setLoading(false);
      this.abortController = null;
    }
  }

  setLoading(isLoading) {
    document.body.classList.toggle(
      'js-ajax-cart-request-in-progress',
      isLoading,
    );
  }
}

customElements.define('sa-cart-note', CartNote);

/* Roadmap GWP signals cart changes via `roadmap:gwp:updated`. Refresh
 * on the leading edge of a burst (matches the original {leading: true,
 * trailing: false} debounce intent — fire immediately, ignore further
 * events within the 1s cooldown) so the UI updates without delay. */
let gwpRefreshCooldown = null;
document.addEventListener('roadmap:gwp:updated', () => {
  if (gwpRefreshCooldown) return;
  refreshCart();
  gwpRefreshCooldown = setTimeout(() => {
    gwpRefreshCooldown = null;
  }, 1000);
});
